import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  Card, Grid, Text, Metric, BadgeDelta, Flex, ProgressBar,
  Title, Badge, List, ListItem
} from "@tremor/react";
import {
  ShoppingBag, ShoppingCart, AlertTriangle, DollarSign, Target,
  Maximize2, X, RefreshCw, Truck,
  PackageCheck, Timer, CheckCircle2,
  Box as BoxIcon, Inbox, Archive, Ban, Warehouse,
  ChevronDown, ChevronRight, Percent
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  AreaChart, Area, Line, Legend, ComposedChart
} from 'recharts';

import RadarDashboard from "../components/RadarDashboard";
import SellerAccountInfoML from "../components/SellerAccountInfoML";
import TicketSummaryDashboard from "../components/TicketSummaryDashboard";
import { Box, Skeleton, Stack } from "@chakra-ui/react";
import ScrollToTop from "../components/ScrollToTop";
import { AuthContext } from "../contexts/auth";
import { dashboardBlocos } from "../services/permission";

import {
  getV3VendasHoje,
  getV3GraficoSemanal,
  getV3GraficoMensal,
  getV3GraficoUltimos30,
  getV3DadosGerais,
  getDadosReputacao,
  getTicketDashboardStats,
  getV3Logistica,
  getV3Produtos,
  getV3Compras,
  getV3ConversaoML,
  getV3Vendedores,
  getReclamacoesMetricas,
  getDevolucoesMetricas
} from "../services/api";
import Preloader from "../components/Preloader";
import DashboardVendedores from "../components/DashboardVendedores";

// --- CONSTANTES E FORMATADORES GLOBAIS ---
const COLORS = ["#4A5568", "#FEF3C7", "#3B82F6", "#10B981", "#EF4444"];
const ORIGIN_COLORS = ["#3b82f6", "#60a5fa", "#10b981", "#f59e0b"];
const SUPPLIER_COLORS = ["#6366f1", "#8b5cf6", "#a855f7", "#ec4899", "#10b981"];
const PROC_COLORS = ["#3b82f6", "#ef4444", "#10b981"];
const REASON_COLORS = ["#ef4444", "#f97316", "#facc15", "#3b82f6", "#6366f1"];

const formatMoney = (val) => `R$ ${parseFloat(val || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

// Atualização automática do Módulo Comercial
const ATUALIZAR_HOJE_MS = 60 * 1000;
const ATUALIZAR_PERIODOS_MS = 5 * 60 * 1000;

// Troca o estado só quando o conteúdo mudou: evita re-render e animação dos gráficos à toa.
const seMudou = (novo) => (atual) => (JSON.stringify(atual) === JSON.stringify(novo) ? atual : novo);

// --- TOOLTIPS PERSONALIZADOS ---
const CustomDualTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 border border-slate-200 shadow-lg rounded-lg z-50">
        <p className="text-xs font-bold text-slate-700 mb-2 border-b pb-1">{label}</p>
        <div className="flex flex-col gap-1">
          <p className="text-xs text-slate-600"><span className="font-semibold">Faturamento:</span> {formatMoney(data.value)}</p>
          <p className="text-xs text-slate-500"><span className="font-semibold">Pedidos:</span> {data.qtd} un</p>
        </div>
      </div>
    );
  }
  return null;
};

const CustomStockTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 border border-slate-200 shadow-lg rounded-lg z-50">
        <p className="text-xs font-bold text-slate-700 mb-1">{label}</p>
        <p className="text-xs text-emerald-600 font-semibold">Valor: {formatMoney(data.valor)}</p>
        <p className="text-xs text-slate-500">Qtd Pedidos: {data.qtd}</p>
      </div>
    );
  }
  return null;
};

const ChartHeader = ({ title, sub, onExpand }) => (
  <Flex justifyContent="between" alignItems="start" className="mb-2">
    <div className="max-w-[85%]">
      <Title className="text-xs font-bold uppercase text-slate-500 tracking-tight leading-none truncate">{title}</Title>
      {sub && <Text className="text-[10px] text-slate-400 uppercase font-bold italic mt-1 truncate">{sub}</Text>}
    </div>
    {onExpand && (
      <button onClick={onExpand} className="p-2 hover:bg-slate-100 rounded-md transition-colors text-slate-400 hover:text-slate-600">
        <Maximize2 size={14} />
      </button>
    )}
  </Flex>
);

// --- COMPONENTES VISUAIS COM SKELETON INTEGRADO ---

const TodaySalesSection = ({ data = {}, loading, setExpanded }) => {
  const hourlyData = data.hourly_trend || [];

  // Lógica da Setinha (BadgeDelta)
  const percent = parseFloat(data.variacao_percentual || 0);
  const deltaType = percent > 0 ? "increase" : percent < 0 ? "decrease" : "unchanged";
  const deltaText = `${Math.abs(percent)}%`; // Remove o sinal negativo para exibir apenas o número com a seta certa

  if (loading) {
    return (
      <Grid numItemsSm={1} numItemsMd={2} numItemsLg={4} className="gap-3 md:gap-4">
        {[1, 2, 3, 4].map(i => <Skeleton key={i} height="96px" borderRadius="lg" />)}
      </Grid>
    );
  }

  return (
    <Grid numItemsSm={1} numItemsMd={2} numItemsLg={4} className="gap-3 md:gap-4">
      {/* Faturamento com Variação Dinâmica */}
      <Card className="px-3 py-2 ring-1 ring-slate-200 shadow-sm overflow-hidden" decoration="left" decorationColor="orange">
        <ChartHeader title="Faturamento Hoje" onExpand={() => setExpanded('faturamento')} />
        <Flex alignItems="start" justifyContent="between">
          <div className="flex items-center space-x-2.5">
            <div className="p-1 bg-orange-100 rounded-lg text-orange-600"><DollarSign size={18} /></div>
            <Metric className="font-bold text-lg md:text-xl">R$ {data.total_vendido || "0,00"}</Metric>
          </div>
          {/* BadgeDelta Dinâmico */}
          <BadgeDelta deltaType={deltaType} size="xs">
            {deltaText}
          </BadgeDelta>
        </Flex>
        <div className="h-7 mt-1.5">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={hourlyData}><Area type="monotone" dataKey="v" stroke="#f97316" fill="#ffedd5" strokeWidth={2} dot={false} /></AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Outros Cards (Mantidos iguais) */}
      <Card className="px-3 py-2 ring-1 ring-slate-200 shadow-sm" decoration="left" decorationColor="blue">
        <Title className="text-xs font-bold uppercase text-slate-500 mb-1.5">Volume de Pedidos</Title>
        <div className="flex items-center space-x-2.5">
          <div className="p-1 bg-blue-100 rounded-lg"><ShoppingBag className="text-blue-600" size={20} /></div>
          <Metric className="font-bold text-lg md:text-xl">{data.total_vendas || 0}</Metric>
        </div>
      </Card>

      <Card className="px-3 py-2 ring-1 ring-slate-200 shadow-sm" decoration="left" decorationColor="emerald">
        <Title className="text-xs font-bold uppercase text-slate-500 mb-1.5">Ticket Médio</Title>
        <div className="flex items-center space-x-2.5">
          <div className="p-1 bg-emerald-100 rounded-lg"><Target className="text-emerald-600" size={18} /></div>
          <Metric className="font-bold text-lg md:text-xl">R$ {data.ticket_medio || "0,00"}</Metric>
        </div>
      </Card>

      <Card className="px-3 py-2 ring-1 ring-slate-200 shadow-sm" decoration="left" decorationColor="red">
        <Title className="text-xs font-bold uppercase text-slate-500 mb-1.5">Vendas Canceladas</Title>
        <div className="flex items-center space-x-2.5">
          <div className="p-1 bg-red-100 rounded-lg"><AlertTriangle className="text-red-600" size={20} /></div>
          <Metric className="font-bold text-lg md:text-xl text-red-600">{data.canceladas || 0}</Metric>
        </div>
      </Card>
    </Grid>
  );
};

const CONVERSAO_PERIODOS = [
  { key: 'hoje', label: 'Hoje' },
  { key: '7d', label: '7 dias' },
  { key: '30d', label: '30 dias' }
];

const fmtTaxaConversao = (v) => (v == null ? '--' : `${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`);
const fmtInteiro = (v) => (v == null ? '--' : Number(v).toLocaleString('pt-BR'));

// Taxa de conversão das contas do ML: vendas ÷ visitas da conta inteira (não de um anúncio).
const ConversionMLSection = ({ data, loading }) => {
  if (loading) {
    return (
      <Grid numItemsSm={1} numItemsLg={3} className="gap-3 md:gap-4 mt-6">
        {[1, 2, 3].map(i => <Skeleton key={i} height="132px" borderRadius="lg" />)}
      </Grid>
    );
  }

  if (!data?.total) {
    return (
      <Card className="mt-6 px-3 py-3 ring-1 ring-slate-200 shadow-sm">
        <Flex justifyContent="start" className="gap-2">
          <AlertTriangle size={16} className="text-amber-500 shrink-0" />
          <Text className="text-xs text-slate-500">Não foi possível carregar a conversão do Mercado Livre.</Text>
        </Flex>
      </Card>
    );
  }

  const cards = [
    { ...data.total, chave: 'total', titulo: 'Total das contas', ok: true },
    ...(data.contas || []).map(c => ({ ...c, chave: `conta-${c.conta}`, titulo: c.nome }))
  ];
  const indisponiveis = data.total.contas_indisponiveis || [];
  const atualizadoEm = data.atualizado_em
    ? new Date(data.atualizado_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 mb-2">
        <Title className="text-xs font-bold uppercase text-slate-500 tracking-tight">Conversão Mercado Livre</Title>
        <Text className="text-[10px] text-slate-400">
          {`Vendas ÷ visitas da conta${atualizadoEm ? ` · atualizado às ${atualizadoEm}` : ''}`}
        </Text>
      </div>

      <Grid numItemsSm={1} numItemsLg={3} className="gap-3 md:gap-4">
        {cards.map(card => (
          <Card
            key={card.chave}
            className="px-3 py-3 ring-1 ring-slate-200 shadow-sm"
            decoration="left"
            decorationColor={card.chave === 'total' ? 'yellow' : 'slate'}
          >
            <Flex justifyContent="between" alignItems="center" className="mb-2 gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="p-1 bg-yellow-100 rounded-lg text-yellow-600 shrink-0"><Percent size={14} /></div>
                <Text className="text-xs font-bold uppercase text-slate-600 truncate">{card.titulo}</Text>
              </div>
              {!card.ok && <Badge size="xs" color="amber">visitas indisponíveis</Badge>}
              {card.chave === 'total' && card.parcial && <Badge size="xs" color="amber">parcial</Badge>}
            </Flex>

            <Grid numItems={3} className="gap-2">
              {CONVERSAO_PERIODOS.map(({ key, label }) => {
                const p = card.periodos?.[key] || {};
                return (
                  <div key={key} className="bg-slate-50 rounded border border-slate-200 px-1.5 py-2 text-center min-w-0">
                    <Text className="text-[9px] font-bold uppercase text-slate-500">{label}</Text>
                    <Metric className="text-base md:text-lg font-black text-slate-800 leading-tight">{fmtTaxaConversao(p.taxa)}</Metric>
                    <Text className="text-[10px] text-slate-600 leading-tight mt-1">{`${fmtInteiro(p.vendas)} vendas`}</Text>
                    <Text className="text-[10px] text-slate-400 leading-tight">{`${fmtInteiro(p.visitas)} visitas`}</Text>
                  </div>
                );
              })}
            </Grid>
          </Card>
        ))}
      </Grid>

      {indisponiveis.length > 0 && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
          <AlertTriangle size={14} className="text-amber-500 mt-0.5 shrink-0" />
          <Text className="text-[11px] text-amber-700">
            {`O Mercado Livre não devolveu as visitas da ${indisponiveis.map(c => `Conta ${c}`).join(' e ')}. O total considera só as contas com visitas.`}
          </Text>
        </div>
      )}
    </div>
  );
};

const ComparisonChartsSection = ({ data, loadingMap, setExpanded }) => {
  const formatCur = (val) => `R$ ${val?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || "0,00"}`;

  // Configuração dos 3 gráficos
  const charts = [
    { id: '30dias', title: "Últimos 30 Dias", current: data.trintaDias?.atual, prev: data.trintaDias?.anterior, chart: data.trintaDias?.chart, color: "#3b82f6", loading: loadingMap.trinta },
    { id: 'semanal', title: "Comparativo Semanal", current: data.semanal?.atual, prev: data.semanal?.anterior, chart: data.semanal?.chart, color: "#f97316", loading: loadingMap.semanal },
    { id: 'mensal', title: "Comparativo Mensal", current: data.mensal?.atual, prev: data.mensal?.anterior, chart: data.mensal?.chart, color: "#10b981", loading: loadingMap.mensal }
  ];

  return (
    <Grid numItemsSm={1} numItemsMd={2} numItemsLg={3} className="gap-4 md:gap-6 mt-6">
      {charts.map((item) => (
        <Card key={item.id} className="shadow-sm border-slate-200 p-4">
          <ChartHeader title={item.title} onExpand={!item.loading ? () => setExpanded(item.id) : undefined} />
          {item.loading ? (
            <Skeleton height="180px" mt={2} borderRadius="md" />
          ) : (
            <>
              <Grid numItems={2} className="mt-2 gap-2 text-center">
                <div className="bg-slate-50 p-2 rounded border"><Text className="text-[9px] font-bold uppercase text-slate-500">Atual</Text><Text className="text-xs font-black">{formatCur(item.current)}</Text></div>
                <div className="bg-slate-50 p-2 rounded border"><Text className="text-[9px] font-bold uppercase text-slate-500">Anterior</Text><Text className="text-xs font-black">{formatCur(item.prev)}</Text></div>
              </Grid>
              <div className="h-44 mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={item.chart || []}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="date" hide={false} stroke="#94a3b8" fontSize={10} />
                    <YAxis width={40} stroke="#94a3b8" fontSize={10} />
                    <Tooltip />
                    <Area type="monotone" dataKey="Atual" stroke={item.color} fill={item.color} fillOpacity={0.1} strokeWidth={2} />
                    <Area type="monotone" dataKey="Anterior" stroke="#cbd5e1" fillOpacity={0} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </Card>
      ))}
    </Grid>
  );
};

const SalesDetailedAnalyticsSection = ({ originData = [], supplierData = [], loading, setExpanded }) => {
  const originHeight = Math.max(250, (originData.length * 40) + 20);
  const supplierHeight = (supplierData.length * 40) + 20;

  if (loading) {
    return (
      <Grid numItemsSm={1} numItemsLg={2} className="gap-4 md:gap-6 mt-6">
        <Skeleton height="300px" borderRadius="lg" />
        <Skeleton height="300px" borderRadius="lg" />
      </Grid>
    );
  }

  return (
    <Grid numItemsSm={1} numItemsLg={2} className="gap-4 md:gap-6 mt-6">
      <Card className="shadow-sm border-slate-200 p-4">
        <ChartHeader title="Origem das Vendas" sub="Últimos 30 Dias" onExpand={() => setExpanded('origem')} />
        <div className="mt-2 overflow-y-auto max-h-[400px] pr-2 custom-scrollbar">
          <div style={{ height: `${originHeight}px` }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={originData} margin={{ left: 10, right: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f1f5f9" />
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={10} width={110} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomDualTooltip />} cursor={{ fill: '#f8fafc' }} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={24}>
                  {originData.map((e, i) => (<Cell key={`co-${i}`} fill={ORIGIN_COLORS[i % 4]} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>

      <Card className="shadow-sm border-slate-200 p-4">
        <ChartHeader title="Vendas por Fornecedor" sub="Últimos 30 Dias" onExpand={() => setExpanded('fornecedor')} />
        <div className="mt-2 overflow-y-auto max-h-[400px] pr-2 custom-scrollbar">
          <div style={{ height: `${supplierHeight}px`, minHeight: '100px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={supplierData} margin={{ left: 10, right: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f1f5f9" />
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={10} width={90} axisLine={false} tickLine={false} interval={0} />
                <Tooltip content={<CustomDualTooltip />} cursor={{ fill: '#f8fafc' }} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={24}>
                  {supplierData.map((e, i) => (<Cell key={`cs-${i}`} fill={SUPPLIER_COLORS[i % SUPPLIER_COLORS.length]} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>
    </Grid>
  );
};

const OrdersStatusAndPendingSection = ({ statusData = [], pendingData = {}, loading, setExpanded }) => {
  if (loading) {
    return (
      <Grid numItemsSm={1} numItemsLg={2} className="gap-4 md:gap-6 mt-6">
        <Skeleton height="250px" borderRadius="lg" />
        <Skeleton height="250px" borderRadius="lg" />
      </Grid>
    );
  }

  return (
    <Grid numItemsSm={1} numItemsLg={2} className="gap-4 md:gap-6 mt-6">
      <Card className="shadow-sm border-slate-200 p-4">
        <ChartHeader title="Status das Vendas" sub="Últimos 14 Dias" onExpand={() => setExpanded('status')} />
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={statusData} barCategoryGap="15%">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="name" stroke="#94a3b8" fontSize={9} fontWeight="bold" interval={0} />
              <YAxis stroke="#94a3b8" fontSize={10} />
              <Tooltip cursor={{ fill: 'transparent' }} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {statusData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % 5]} />))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <Card className="shadow-sm border-slate-200 p-4 text-center">
        <Title className="text-sm font-bold uppercase text-slate-500 mb-4">Pendências Comerciais</Title>
        <Grid numItemsSm={2} numItemsMd={3} className="gap-2">
          {[{ label: "Atrasadas", val: pendingData.totalVendasAtrasadas, color: "red" }, { label: "Furos", val: pendingData.totalFuroEstoque, color: "red" }, { label: "Confirmar", val: pendingData.totalPendentes, color: "yellow" }, { label: "Envio", val: pendingData.totalProblemaEnvio, color: "slate" }, { label: "Preço", val: pendingData.totalErroPrecificacao, color: "purple" }, { label: "Outros", val: pendingData.totalOutrosProblemas, color: "blue" }].map(item => (
            <div key={item.label} className={`bg-${item.color}-100 p-2 rounded border border-${item.color}-200 flex flex-col items-center justify-center`}><Text className={`text-[9px] font-black text-${item.color}-700 uppercase`}>{item.label}</Text><Metric className={`text-lg font-black text-${item.color}-900`}>{item.val || 0}</Metric></div>
          ))}
        </Grid>
      </Card>
    </Grid>
  );
};

const LongTermAnalyticsSection = ({ monthlyData, yearlyData, physicalData, loading, setExpanded }) => {
  if (loading) {
    return (
      <Grid numItemsSm={1} numItemsLg={3} className="gap-4 md:gap-6 mt-6">
        <Skeleton height="280px" borderRadius="lg" />
        <Skeleton height="280px" borderRadius="lg" />
        <Skeleton height="280px" borderRadius="lg" />
      </Grid>
    );
  }

  return (
    <Grid numItemsSm={1} numItemsLg={3} className="gap-4 md:gap-6 mt-6">
      <Card className="shadow-sm border-slate-200 p-4">
        <ChartHeader title="Evolução Mensal" sub="Financeiro (Ano x Ano)" onExpand={() => setExpanded('evolucao')} />
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="mes" stroke="#94a3b8" fontSize={10} fontWeight="bold" />
              <YAxis stroke="#94a3b8" fontSize={10} tickFormatter={(val) => `R$${val / 1000}k`} />
              <Tooltip formatter={(value) => formatMoney(value)} />
              <Area type="monotone" dataKey="Ano Atual" stroke="#8b5cf6" fill="#ddd6fe" fillOpacity={0.2} strokeWidth={3} dot={{ r: 3, fill: "#8b5cf6" }} />
              <Area type="monotone" dataKey="Ano Anterior" stroke="#cbd5e1" fillOpacity={0} strokeWidth={2} strokeDasharray="5 5" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="shadow-sm border-slate-200 p-4">
        <ChartHeader title="Comparativo por Ano" sub="Faturamento Total" onExpand={() => setExpanded('anual')} />
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={yearlyData} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="ano" stroke="#94a3b8" fontSize={10} fontWeight="bold" />
              <YAxis stroke="#94a3b8" fontSize={10} tickFormatter={(val) => `R$${val / 1000}k`} />
              <Tooltip formatter={(value) => formatMoney(value)} cursor={{ fill: '#f8fafc' }} />
              <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                {yearlyData && yearlyData.map((e, i) => (
                  <Cell key={`c-${i}`} fill={String(e.ano) === String(new Date().getFullYear()) ? "#3b82f6" : "#94a3b8"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="shadow-sm border-slate-200 p-4">
        <ChartHeader title="Vendas Estoque Físico" sub="Valor e Volume" onExpand={() => setExpanded('estoque_fisico')} />
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={physicalData} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="mes" stroke="#94a3b8" fontSize={9} />
              <YAxis stroke="#94a3b8" fontSize={10} tickFormatter={(val) => `R$${val / 1000}k`} />
              <Tooltip content={<CustomStockTooltip />} cursor={{ fill: '#f8fafc' }} />
              <Bar dataKey="valor" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </Grid>
  );
};


const LogisticsModule = ({ data = {}, loading }) => {
  if (loading) {
    return (
      <Grid numItemsSm={1} numItemsMd={2} numItemsLg={4} className="gap-4 md:gap-6">
        {[1, 2, 3, 4].map(i => <Skeleton key={i} height="100px" borderRadius="lg" />)}
      </Grid>
    );
  }

  return (
    <Grid numItemsSm={2} numItemsMd={3} numItemsLg={5} className="gap-3 md:gap-4">
      <Card className="p-4 ring-1 ring-slate-200 shadow-sm" decoration="top" decorationColor="amber">
        <Flex justifyContent="start" className="space-x-3"><div className="p-2 bg-amber-100 rounded-lg text-amber-600"><Truck size={20} /></div><div><Text className="text-[10px] font-bold uppercase text-slate-500">Para Despachar Hoje</Text><Metric className="text-xl font-black">{data.para_despachar_hoje || 0}</Metric></div></Flex>
      </Card>
      <Card className="p-4 ring-1 ring-slate-200 shadow-sm" decoration="top" decorationColor="emerald">
        <Flex justifyContent="start" className="space-x-3"><div className="p-2 bg-emerald-100 rounded-lg text-emerald-600"><Truck size={20} /></div><div><Text className="text-[10px] font-bold uppercase text-slate-500">Despachadas Hoje</Text><Metric className="text-xl font-black">{data.despachadas || 0}</Metric></div></Flex>
      </Card>
      <Card className="p-4 ring-1 ring-slate-200 shadow-sm" decoration="top" decorationColor="red">
        <Flex justifyContent="start" className="space-x-3"><div className="p-2 bg-red-100 rounded-lg text-red-600"><Timer size={20} /></div><div><Text className="text-[10px] font-bold uppercase text-slate-500">Atrasadas</Text><Metric className="text-xl font-black text-red-600">{data.atrasadas || 0}</Metric></div></Flex>
      </Card>
      <Card className="p-4 ring-1 ring-slate-200 shadow-sm" decoration="top" decorationColor="orange">
        <Flex justifyContent="start" className="space-x-3"><div className="p-2 bg-orange-100 rounded-lg text-orange-600"><PackageCheck size={20} /></div><div><Text className="text-[10px] font-bold uppercase text-slate-500">Problemas Conferência</Text><Metric className="text-xl font-black">{data.problemas || 0}</Metric></div></Flex>
      </Card>
      <Card className="p-4 ring-1 ring-slate-200 shadow-sm" decoration="top" decorationColor="blue">
        <Flex justifyContent="start" className="space-x-3"><div className="p-2 bg-blue-100 rounded-lg text-blue-600"><CheckCircle2 size={20} /></div><div><Text className="text-[10px] font-bold uppercase text-slate-500">Devoluções Recebidas</Text><Metric className="text-xl font-black">{data.devolucoes_recebidas || 0}</Metric></div></Flex>
      </Card>
    </Grid>
  );
};

const FORNECEDOR_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#64748b'];
const STATUS_ANUNCIO_COLORS = { active: '#10b981', paused: '#f59e0b', closed: '#ef4444', under_review: '#6366f1', inactive: '#94a3b8' };

const MiniBar = ({ value, max, color }) => (
  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${max > 0 ? (value / max) * 100 : 0}%`, backgroundColor: color }} />
  </div>
);

const ProductsModule = ({ data = {}, loading, setExpanded }) => {
  const cards             = data.cards || {};
  const top20Raw          = data.top20_vendidos || [];
  const top10ReceitaRaw   = data.top10_receita || [];
  const top10Furo         = data.top10_furo || [];
  const estoqueFornecedor = data.estoque_fornecedor || [];
  const anuncioStatus     = data.anuncio_status || [];

  const skuLabel = (item, max = 30) => {
    const full = item.sku ? `(${item.sku}) - ${item.name || ''}` : (item.name || '');
    return full.length > max ? full.slice(0, max) + '…' : full;
  };
  const top20        = top20Raw.map(item => ({ ...item, label: `(${item.sku || '?'}) - ${item.name}` }));
  const top10Receita = top10ReceitaRaw.map(item => ({ ...item, label: `(${item.sku || '?'}) - ${item.name}` }));
  const totalForn  = estoqueFornecedor.reduce((s, f) => s + f.value, 0);
  const totalAnunc = anuncioStatus.reduce((s, a) => s + a.value, 0);
  const maxTop20   = top20[0]?.value || 1;
  const maxFuro    = top10Furo[0]?.value || 1;

  if (loading) {
    return (
      <div className="space-y-6">
        <Grid numItemsSm={2} numItemsLg={4} className="gap-4 md:gap-6">
          {[1,2,3,4].map(i => <Skeleton key={i} height="90px" borderRadius="lg" />)}
        </Grid>
        <Skeleton height="520px" borderRadius="lg" />
        <Grid numItemsSm={1} numItemsLg={2} className="gap-4 md:gap-6">
          <Skeleton height="320px" borderRadius="lg" />
          <Skeleton height="320px" borderRadius="lg" />
        </Grid>
        <Grid numItemsSm={1} numItemsLg={2} className="gap-4 md:gap-6">
          <Skeleton height="220px" borderRadius="lg" />
          <Skeleton height="220px" borderRadius="lg" />
        </Grid>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* ── Cards de resumo ── */}
      <Grid numItemsSm={2} numItemsLg={4} className="gap-4 md:gap-6">
        <Card decoration="top" decorationColor="fuchsia" className="p-4 ring-1 ring-slate-200 shadow-sm">
          <Flex justifyContent="start" className="space-x-3">
            <div className="p-2 bg-fuchsia-100 rounded-lg text-fuchsia-600"><Archive size={20} /></div>
            <div>
              <Text className="text-[10px] font-bold uppercase text-slate-500">Catálogo</Text>
              <Metric className="text-2xl font-black">{(cards.total_catalogo || 0).toLocaleString('pt-BR')}</Metric>
            </div>
          </Flex>
          <Text className="text-[10px] text-slate-400 mt-2 pl-1">Total de produtos cadastrados</Text>
        </Card>

        <Card decoration="top" decorationColor="emerald" className="p-4 ring-1 ring-slate-200 shadow-sm">
          <Flex justifyContent="start" className="space-x-3">
            <div className="p-2 bg-emerald-100 rounded-lg text-emerald-600"><Warehouse size={20} /></div>
            <div>
              <Text className="text-[10px] font-bold uppercase text-slate-500">Estoque TAGA</Text>
              <Metric className="text-2xl font-black text-emerald-700">{(cards.com_estoque_taga || 0).toLocaleString('pt-BR')}</Metric>
            </div>
          </Flex>
          <Text className="text-[10px] text-slate-400 mt-2 pl-1">Produtos com estoque físico</Text>
        </Card>

        <Card decoration="top" decorationColor="red" className="p-4 ring-1 ring-slate-200 shadow-sm">
          <Flex justifyContent="start" className="space-x-3">
            <div className="p-2 bg-red-100 rounded-lg text-red-600"><Ban size={20} /></div>
            <div>
              <Text className="text-[10px] font-bold uppercase text-slate-500">Bloqueados</Text>
              <Metric className="text-2xl font-black text-red-600">{(cards.bloqueados || 0).toLocaleString('pt-BR')}</Metric>
            </div>
          </Flex>
          <Text className="text-[10px] text-slate-400 mt-2 pl-1">Bloqueados permanentemente</Text>
        </Card>

        <Card decoration="top" decorationColor="amber" className="p-4 ring-1 ring-slate-200 shadow-sm">
          <Flex justifyContent="start" className="space-x-3">
            <div className="p-2 bg-amber-100 rounded-lg text-amber-600"><Timer size={20} /></div>
            <div>
              <Text className="text-[10px] font-bold uppercase text-slate-500">Bloqueio Temp.</Text>
              <Metric className="text-2xl font-black text-amber-600">{(cards.bloqueados_temp || 0).toLocaleString('pt-BR')}</Metric>
            </div>
          </Flex>
          <Text className="text-[10px] text-slate-400 mt-2 pl-1">Com bloqueio temporário ativo</Text>
        </Card>
      </Grid>

      {/* ── Top 20 Mais Vendidos ── */}
      <Card className="p-4 ring-1 ring-slate-200 shadow-sm">
        <ChartHeader title="Top 20 Mais Vendidos" sub="Período total · por unidades" onExpand={setExpanded ? () => setExpanded('prod_top20') : null} />
        {top20.length === 0 ? (
          <Text className="text-slate-400 text-xs mt-4">Sem dados no período.</Text>
        ) : (
          <div style={{ height: `${top20.length * 28 + 40}px` }} className="mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={top20} layout="vertical" margin={{ top: 0, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                <XAxis type="number" fontSize={10} stroke="#94a3b8" />
                <YAxis dataKey="label" type="category" width={210} fontSize={10} stroke="#94a3b8"
                  tickFormatter={(v) => v && v.length > 32 ? v.slice(0, 32) + '…' : v} />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const p = payload[0]?.payload || {};
                  return (
                    <div className="bg-white border border-slate-200 shadow-lg rounded-lg p-3">
                      <p className="text-xs font-bold text-slate-700 mb-1">{p.label}</p>
                      <p className="text-xs text-indigo-600 font-semibold">{payload[0]?.value} unidades</p>
                    </div>
                  );
                }} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {top20.map((_, i) => (
                    <Cell key={i} fill={`hsl(${243 - i * 4}, 65%, ${62 - i * 1.2}%)`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* ── Top 10 Receita + Top 10 Furo ── */}
      <Grid numItemsSm={1} numItemsLg={2} className="gap-4 md:gap-6">
        <Card className="p-4 ring-1 ring-slate-200 shadow-sm">
          <ChartHeader title="Top 10 por Receita" sub="Período total · R$" onExpand={setExpanded ? () => setExpanded('prod_receita') : null} />
          {top10Receita.length === 0 ? (
            <Text className="text-slate-400 text-xs mt-4">Sem dados no período.</Text>
          ) : (
            <div style={{ height: `${top10Receita.length * 32 + 40}px` }} className="mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={top10Receita} layout="vertical" margin={{ top: 0, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                  <XAxis type="number" fontSize={10} stroke="#94a3b8"
                    tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                  <YAxis dataKey="label" type="category" width={190} fontSize={10} stroke="#94a3b8"
                    tickFormatter={(v) => v && v.length > 28 ? v.slice(0, 28) + '…' : v} />
                  <Tooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const p = payload[0]?.payload || {};
                    return (
                      <div className="bg-white border border-slate-200 shadow-lg rounded-lg p-3">
                        <p className="text-xs font-bold text-slate-700 mb-1">{p.label}</p>
                        <p className="text-xs text-emerald-600 font-semibold">{formatMoney(payload[0]?.value)}</p>
                        <p className="text-xs text-slate-500">{p.qtd} un vendidas</p>
                      </div>
                    );
                  }} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {top10Receita.map((_, i) => (
                      <Cell key={i} fill={`hsl(${158 + i * 3}, 58%, ${46 + i * 1.5}%)`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card className="p-4 ring-1 ring-slate-200 shadow-sm">
          <ChartHeader title="Top 10 Furo de Estoque" sub="Período total · vendidos sem estoque TAGA" onExpand={setExpanded ? () => setExpanded('prod_furo') : null} />
          {top10Furo.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 gap-2">
              <CheckCircle2 size={28} className="text-emerald-400" />
              <Text className="text-slate-400 text-xs">Nenhum furo de estoque no período.</Text>
            </div>
          ) : (
            <div className="mt-4 space-y-2">
              {top10Furo.map((item, i) => (
                <div key={i} className="group">
                  <Flex justifyContent="between" className="mb-1">
                    <Text className="text-xs font-medium text-slate-700 truncate max-w-[70%]" title={`(${item.sku || '?'}) - ${item.name}`}>
                      {i + 1}. {skuLabel(item, 32)}
                    </Text>
                    <Text className="text-xs font-bold text-orange-600 shrink-0">{item.value} un</Text>
                  </Flex>
                  <MiniBar value={item.value} max={maxFuro} color={`hsl(${20 + i * 5}, 85%, ${55 - i * 2}%)`} />
                </div>
              ))}
            </div>
          )}
        </Card>
      </Grid>

      {/* ── Distribuição Fornecedor + Anúncios Status ── */}
      <Grid numItemsSm={1} numItemsLg={2} className="gap-4 md:gap-6">
        <Card className="p-4 ring-1 ring-slate-200 shadow-sm">
          <ChartHeader title="Distribuição de Estoque" sub="Por fornecedor · unidades" />
          <div className="mt-4 space-y-4">
            {estoqueFornecedor.map((f, i) => (
              <div key={i}>
                <Flex justifyContent="between" className="mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: FORNECEDOR_COLORS[i % FORNECEDOR_COLORS.length] }} />
                    <Text className="text-xs font-semibold text-slate-700">{f.name}</Text>
                  </div>
                  <div className="flex items-center gap-2">
                    <Text className="text-xs text-slate-500">{f.value.toLocaleString('pt-BR')} un</Text>
                    <Text className="text-[10px] font-bold text-slate-400 w-10 text-right">
                      {totalForn > 0 ? ((f.value / totalForn) * 100).toFixed(1) : 0}%
                    </Text>
                  </div>
                </Flex>
                <MiniBar value={f.value} max={totalForn} color={FORNECEDOR_COLORS[i % FORNECEDOR_COLORS.length]} />
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-4 ring-1 ring-slate-200 shadow-sm">
          <ChartHeader title="Anúncios ML por Status" sub="Total de anúncios cadastrados" />
          <div className="mt-4 space-y-4">
            {anuncioStatus.map((a, i) => {
              const color = STATUS_ANUNCIO_COLORS[a.key] || '#94a3b8';
              return (
                <div key={i}>
                  <Flex justifyContent="between" className="mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                      <Text className="text-xs font-semibold text-slate-700">{a.name}</Text>
                    </div>
                    <div className="flex items-center gap-2">
                      <Text className="text-xs text-slate-500">{a.value.toLocaleString('pt-BR')}</Text>
                      <Text className="text-[10px] font-bold text-slate-400 w-10 text-right">
                        {totalAnunc > 0 ? ((a.value / totalAnunc) * 100).toFixed(1) : 0}%
                      </Text>
                    </div>
                  </Flex>
                  <MiniBar value={a.value} max={totalAnunc} color={color} />
                </div>
              );
            })}
          </div>
        </Card>
      </Grid>

    </div>
  );
};

const COMPRAS_COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#14b8a6', '#f97316', '#94a3b8'];

const formatMoneyCurto = (v) => {
  const n = Number(v || 0);
  return Math.abs(n) >= 1000 ? `R$ ${Math.round(n / 1000)}k` : `R$ ${Math.round(n)}`;
};

// Tooltip do empilhado: só quem comprou no mês, do maior pro menor, com o total.
const ComprasTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const linhas = payload.filter(p => (p.value || 0) > 0).sort((a, b) => b.value - a.value);
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  const parcial = payload[0]?.payload?.parcial;

  return (
    <div className="bg-white p-3 border border-slate-200 shadow-lg rounded-lg z-50 max-w-[260px]">
      <p className="text-xs font-bold text-slate-700 mb-1.5 border-b pb-1">
        {label}{parcial ? ' · mês em curso' : ''}
      </p>
      {linhas.map(p => (
        <div key={p.dataKey} className="flex items-center justify-between gap-3">
          <span className="text-[11px] text-slate-600 truncate flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
            {p.dataKey}
          </span>
          <span className="text-[11px] font-semibold text-slate-700 whitespace-nowrap">{formatMoney(p.value)}</span>
        </div>
      ))}
      <div className="flex items-center justify-between gap-3 border-t mt-1.5 pt-1">
        <span className="text-[11px] font-bold text-slate-500">Total</span>
        <span className="text-[11px] font-bold text-slate-800">{formatMoney(total)}</span>
      </div>
    </div>
  );
};

// Variação do fornecedor no mês contra o mesmo pedaço do mês anterior.
const ComprasDelta = ({ item }) => {
  if (!item.mes_anterior_ate_dia && item.mes_atual > 0) {
    return <Badge size="xs" color="indigo">novo no mês</Badge>;
  }
  if (item.variacao == null) {
    return <Badge size="xs" color="slate">sem compra</Badge>;
  }
  const tipo = item.variacao > 0 ? 'increase' : item.variacao < 0 ? 'decrease' : 'unchanged';
  return <BadgeDelta deltaType={tipo} size="xs">{`${Math.abs(item.variacao)}%`}</BadgeDelta>;
};

const PurchasesModule = ({ data = {}, loading, setExpanded }) => {
  const cards       = data.cards || {};
  const mesAtual    = cards.mes_atual || {};
  const mesAnterior = cards.mes_anterior || {};
  const lider       = cards.lider_mes;
  const evolucao    = data.evolucao || [];
  const series      = data.series_grafico || [];
  const ranking     = data.ranking || [];
  const avisos      = data.avisos || [];
  const diaCorte    = data.periodo?.dia_corte;

  const corPorNome = Object.fromEntries(series.map((nome, i) => [nome, COMPRAS_COLORS[i % COMPRAS_COLORS.length]]));
  const corDe = (nome) => corPorNome[nome] || '#cbd5e1';

  // Mês em curso ganha asterisco: comparar barra cheia com barra pela metade engana.
  const chartData = evolucao.map(e => ({ ...e, label: e.parcial ? `${e.label}*` : e.label }));
  const totalPeriodo = cards.total_periodo || 0;

  const deltaMes = cards.variacao;
  const tipoDelta = deltaMes == null ? 'unchanged' : deltaMes > 0 ? 'increase' : deltaMes < 0 ? 'decrease' : 'unchanged';

  if (loading) {
    return (
      <div className="space-y-6">
        <Grid numItemsSm={2} numItemsLg={4} className="gap-3 md:gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} height="96px" borderRadius="lg" />)}
        </Grid>
        <Skeleton height="340px" borderRadius="lg" />
        <Grid numItemsSm={1} numItemsLg={2} className="gap-4 md:gap-6">
          <Skeleton height="300px" borderRadius="lg" />
          <Skeleton height="300px" borderRadius="lg" />
        </Grid>
      </div>
    );
  }

  if (!evolucao.length || totalPeriodo <= 0) {
    return (
      <Card className="p-8 ring-1 ring-slate-200 shadow-sm">
        <Flex justifyContent="center" className="flex-col gap-2 text-center">
          <div className="p-3 bg-slate-100 rounded-full text-slate-400"><ShoppingCart size={24} /></div>
          <Text className="text-sm font-semibold text-slate-600">Nenhuma compra no período</Text>
          <Text className="text-xs text-slate-400">Listas aprovadas e itens de cotação comprados aparecem aqui.</Text>
        </Flex>
      </Card>
    );
  }

  return (
    <div className="space-y-6">

      {/* ── Cards do mês ── */}
      <Grid numItemsSm={1} numItemsMd={2} numItemsLg={4} className="gap-3 md:gap-4">
        <Card className="px-3 py-2 ring-1 ring-slate-200 shadow-sm" decoration="left" decorationColor="indigo">
          <ChartHeader title={`Comprado em ${mesAtual.label || '--'}`} onExpand={() => setExpanded('compras_evolucao')} />
          <Flex alignItems="start" justifyContent="between">
            <div className="flex items-center space-x-2.5 min-w-0">
              <div className="p-1 bg-indigo-100 rounded-lg text-indigo-600 shrink-0"><ShoppingCart size={18} /></div>
              <Metric className="font-bold text-lg md:text-xl truncate">{formatMoney(mesAtual.valor)}</Metric>
            </div>
            <BadgeDelta deltaType={tipoDelta} size="xs">{deltaMes == null ? '--' : `${Math.abs(deltaMes)}%`}</BadgeDelta>
          </Flex>
          <Text className="text-[10px] text-slate-400 mt-1.5">
            {`vs ${formatMoney(mesAnterior.valor_ate_dia)} de ${mesAnterior.label || '--'}`}
            {diaCorte ? ` até o dia ${diaCorte}` : ''}
          </Text>
        </Card>

        <Card className="px-3 py-2 ring-1 ring-slate-200 shadow-sm" decoration="left" decorationColor="blue">
          <Title className="text-xs font-bold uppercase text-slate-500 mb-1.5">Itens Comprados</Title>
          <Flex alignItems="center" justifyContent="start" className="space-x-2.5">
            <div className="p-1 bg-blue-100 rounded-lg text-blue-600 shrink-0"><PackageCheck size={18} /></div>
            <Metric className="font-bold text-lg md:text-xl">{(mesAtual.itens || 0).toLocaleString('pt-BR')}</Metric>
          </Flex>
          <Text className="text-[10px] text-slate-400 mt-1.5">
            {`${(mesAtual.listas || 0).toLocaleString('pt-BR')} listas aprovadas no mês`}
          </Text>
        </Card>

        <Card className="px-3 py-2 ring-1 ring-slate-200 shadow-sm" decoration="left" decorationColor="emerald">
          <Title className="text-xs font-bold uppercase text-slate-500 mb-1.5">Média Mensal</Title>
          <Flex alignItems="center" justifyContent="start" className="space-x-2.5">
            <div className="p-1 bg-emerald-100 rounded-lg text-emerald-600 shrink-0"><DollarSign size={18} /></div>
            <Metric className="font-bold text-lg md:text-xl truncate">{formatMoney(cards.media_mensal)}</Metric>
          </Flex>
          <Text className="text-[10px] text-slate-400 mt-1.5">
            {`Meses fechados · ${formatMoney(totalPeriodo)} no período`}
          </Text>
        </Card>

        <Card className="px-3 py-2 ring-1 ring-slate-200 shadow-sm" decoration="left" decorationColor="fuchsia">
          <Title className="text-xs font-bold uppercase text-slate-500 mb-1.5">Líder do Mês</Title>
          <Flex alignItems="center" justifyContent="start" className="space-x-2.5">
            <div className="p-1 bg-fuchsia-100 rounded-lg text-fuchsia-600 shrink-0"><Warehouse size={18} /></div>
            <Metric className="font-bold text-base md:text-lg truncate">{lider?.nome || '--'}</Metric>
          </Flex>
          <Text className="text-[10px] text-slate-400 mt-1.5">
            {lider ? `${formatMoney(lider.valor)} · ${lider.participacao}% do mês` : 'Sem compra registrada no mês'}
          </Text>
        </Card>
      </Grid>

      {avisos.length > 0 && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle size={14} className="text-amber-500 mt-0.5 shrink-0" />
          <Text className="text-[11px] text-amber-700">{avisos.join(' ')}</Text>
        </div>
      )}

      {/* ── Evolução mensal empilhada por fornecedor ── */}
      <Card className="p-4 ring-1 ring-slate-200 shadow-sm">
        <ChartHeader
          title="Evolução das Compras por Fornecedor"
          sub="Lista de compras aprovada + cobertura de furos"
          onExpand={() => setExpanded('compras_evolucao')}
        />
        <div className="h-[280px] md:h-[340px] mt-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={52} tickFormatter={formatMoneyCurto} />
              <Tooltip content={<ComprasTooltip />} cursor={{ fill: '#f8fafc' }} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10, paddingTop: 8 }} />
              {series.map((nome, i) => (
                <Bar
                  key={nome}
                  dataKey={nome}
                  stackId="compras"
                  fill={COMPRAS_COLORS[i % COMPRAS_COLORS.length]}
                  radius={i === series.length - 1 ? [3, 3, 0, 0] : 0}
                  maxBarSize={56}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
        {chartData.some(e => e.parcial) && (
          <Text className="text-[10px] text-slate-400 italic mt-1">* mês em curso, ainda incompleto</Text>
        )}
      </Card>

      {/* ── Ranking do período + origem da compra ── */}
      <Grid numItemsSm={1} numItemsLg={2} className="gap-4 md:gap-6">
        <Card className="p-4 ring-1 ring-slate-200 shadow-sm">
          <ChartHeader
            title="Fornecedores no Período"
            sub={`Variação do mês vs mesmo período do anterior`}
            onExpand={() => setExpanded('compras_ranking')}
          />
          <div className="mt-4 space-y-3.5 max-h-[420px] overflow-y-auto pr-1 custom-scrollbar">
            {ranking.map((f) => (
              <div key={`${f.fornecedor_id || 'c'}-${f.nome}`}>
                <Flex justifyContent="between" className="mb-1.5 gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: corDe(f.nome) }} />
                    <Text className="text-xs font-semibold text-slate-700 truncate">{f.nome}</Text>
                    {f.avulso && <Badge size="xs" color="slate">cotação</Badge>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Text className="text-xs font-semibold text-slate-600">{formatMoney(f.mes_atual)}</Text>
                    <ComprasDelta item={f} />
                  </div>
                </Flex>
                <MiniBar value={f.total} max={totalPeriodo} color={corDe(f.nome)} />
                <Text className="text-[10px] text-slate-400 mt-1">
                  {`${formatMoney(f.total)} no período · ${f.participacao || 0}% · ${(f.itens || 0).toLocaleString('pt-BR')} itens`}
                </Text>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-4 ring-1 ring-slate-200 shadow-sm">
          <ChartHeader title="Origem da Compra" sub="Lista de compras × cobertura de furos" />
          <div className="h-[240px] md:h-[280px] mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={52} tickFormatter={formatMoneyCurto} />
                <Tooltip cursor={{ fill: '#f8fafc' }} formatter={(v) => formatMoney(v)} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10, paddingTop: 8 }} />
                <Bar dataKey="valor_lista" name="Lista de compras" stackId="origem" fill="#6366f1" maxBarSize={56} />
                <Bar dataKey="valor_cobertura" name="Cobertura de furos" stackId="origem" fill="#f59e0b" radius={[3, 3, 0, 0]} maxBarSize={56} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <Text className="text-[10px] text-slate-400 mt-1">
            {`Cobertura de furos no período: ${formatMoney(data.cobertura?.valor_periodo)}`}
          </Text>
        </Card>
      </Grid>

    </div>
  );
};

const MotivosBarChart = ({ data, color }) => {
  if (!data || data.length === 0) {
    return <Flex className="h-[240px] items-center justify-center"><Text className="text-xs text-slate-400">Sem motivos registrados</Text></Flex>;
  }
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
        <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
        <YAxis type="category" dataKey="motivo" width={130} tick={{ fontSize: 9, fill: '#64748b' }} interval={0} />
        <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
        <Bar dataKey="qtd" name="Qtd" fill={color} radius={[0, 4, 4, 0]} barSize={14} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const AfterSalesModule = ({ data, charts, loading }) => {
  const fmtNum = (v) => (v == null ? '--' : Number(v).toLocaleString('pt-BR'));
  const fmtPct = (v) => (v == null ? '--%' : `${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`);
  const val = (node) => (loading ? <Skeleton height="28px" width="60px" mt={1} /> : node);
  const chartOrSkeleton = (node) => (loading ? <Skeleton height="240px" mt={1} borderRadius="md" /> : node);
  return (
    <div className="flex flex-col gap-4 md:gap-6">
      <Grid numItemsSm={1} numItemsMd={2} numItemsLg={4} className="gap-4 md:gap-6">
        <Card className="p-4 ring-1 ring-slate-200 shadow-sm border-t-4 border-t-violet-500">
          <Text className="text-[10px] font-bold uppercase text-slate-500">Reclamações Abertas</Text>
          {val(<Metric className="text-2xl font-black text-violet-700">{fmtNum(data?.reclAbertas)}</Metric>)}
          <Text className="text-[10px] text-slate-400 mt-1">em aberto</Text>
        </Card>
        <Card className="p-4 ring-1 ring-slate-200 shadow-sm border-t-4 border-t-red-500">
          <Text className="text-[10px] font-bold uppercase text-slate-500">Taxa Reclamação</Text>
          {val(<Metric className="text-2xl font-black text-red-600">{fmtPct(data?.taxaRecl)}</Metric>)}
          <Text className="text-[10px] text-slate-400 mt-1">últimos 12 meses</Text>
        </Card>
        <Card className="p-4 ring-1 ring-slate-200 shadow-sm border-t-4 border-t-orange-500">
          <Text className="text-[10px] font-bold uppercase text-slate-500">Devoluções</Text>
          {val(<Metric className="text-2xl font-black text-orange-600">{fmtNum(data?.devTotal)}</Metric>)}
          <Text className="text-[10px] text-slate-400 mt-1">últimos 12 meses</Text>
        </Card>
        <Card className="p-4 ring-1 ring-slate-200 shadow-sm border-t-4 border-t-blue-500">
          <Text className="text-[10px] font-bold uppercase text-slate-500">Taxa Devolução</Text>
          {val(<Metric className="text-2xl font-black text-blue-600">{fmtPct(data?.taxaDev)}</Metric>)}
          <Text className="text-[10px] text-slate-400 mt-1">últimos 12 meses</Text>
        </Card>
      </Grid>

      <Grid numItemsSm={1} numItemsLg={2} className="gap-4 md:gap-6">
        <Card className="p-4 ring-1 ring-slate-200 shadow-sm">
          <Text className="text-xs font-bold uppercase text-slate-600 mb-3">Taxa por mês — Reclamação × Devolução</Text>
          {chartOrSkeleton(
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={charts?.taxaTrend || []} margin={{ top: 4, right: 8, left: -8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <YAxis yAxisId="taxa" tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={(v) => `${v}%`} />
                <YAxis yAxisId="pedidos" orientation="right" tick={{ fontSize: 10, fill: '#cbd5e1' }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v, n) => (n === 'Pedidos' ? Number(v).toLocaleString('pt-BR') : `${v}%`)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="pedidos" dataKey="Pedidos" fill="#e2e8f0" radius={[3, 3, 0, 0]} />
                <Line yAxisId="taxa" type="monotone" dataKey="Reclamação" stroke="#ef4444" strokeWidth={2} dot={false} />
                <Line yAxisId="taxa" type="monotone" dataKey="Devolução" stroke="#3b82f6" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
          <Text className="text-[10px] text-slate-400 mt-1">barras = pedidos do mês; taxa por safra (últimos ~2 meses ainda maturando)</Text>
        </Card>

        <Card className="p-4 ring-1 ring-slate-200 shadow-sm">
          <Text className="text-xs font-bold uppercase text-slate-600 mb-3">Volume por mês</Text>
          {chartOrSkeleton(
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={charts?.volumeTrend || []} margin={{ top: 4, right: 8, left: -8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <YAxis yAxisId="qtd" tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
                <YAxis yAxisId="pedidos" orientation="right" tick={{ fontSize: 10, fill: '#cbd5e1' }} allowDecimals={false} />
                <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => Number(v).toLocaleString('pt-BR')} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="qtd" dataKey="Reclamações" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
                <Bar yAxisId="qtd" dataKey="Devoluções" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                <Line yAxisId="pedidos" type="monotone" dataKey="Pedidos" stroke="#94a3b8" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
          <Text className="text-[10px] text-slate-400 mt-1">linha = pedidos do mês (eixo à direita)</Text>
        </Card>
      </Grid>

      <Grid numItemsSm={1} numItemsLg={2} className="gap-4 md:gap-6">
        <Card className="p-4 ring-1 ring-slate-200 shadow-sm">
          <Text className="text-xs font-bold uppercase text-slate-600 mb-3">Top motivos — Reclamações</Text>
          {chartOrSkeleton(<MotivosBarChart data={charts?.motivosRecl} color="#ef4444" />)}
        </Card>
        <Card className="p-4 ring-1 ring-slate-200 shadow-sm">
          <Text className="text-xs font-bold uppercase text-slate-600 mb-3">Top motivos — Devoluções</Text>
          {chartOrSkeleton(<MotivosBarChart data={charts?.motivosDev} color="#f59e0b" />)}
        </Card>
      </Grid>
    </div>
  );
};

// --- COMPONENTE PRINCIPAL ---
const DashboardV3 = () => {
  const dataFetchedRef = useRef(false);
  const { user } = useContext(AuthContext);
  const blocos = dashboardBlocos(user);
  const pode = (bloco) => blocos.includes(bloco);
  const [expandedBlock, setExpandedBlock] = useState(null);
  const [reloading, setReloading] = useState(false);

  // --- STATES DE LOADING GRANULARES ---
  const [loadingMap, setLoadingMap] = useState({
    today: true,
    semanal: true,
    mensal: true,
    trinta: true,
    geral: true,
    reputacao: true,
    tickets: true,
    logistica: true,
    produtos: true,
    compras: true,
    posVenda: true,
    conversao: true,
    vendedores: true
  });

  // States de Dados
  const [vendasHoje, setVendasHoje] = useState({});
  const [dadosGerais, setDadosGerais] = useState({
    trintaDias: {}, semanal: {}, mensal: {}, dash: {},
    originData: [], supplierData: [], yearlyData: [], monthlyEvolution: [], physicalStockData: []
  });

  const [dadosLogistica, setDadosLogistica] = useState({});
  const [dadosProdutos, setDadosProdutos] = useState({});
  const [dadosCompras, setDadosCompras] = useState({});
  const [conversaoML, setConversaoML] = useState(null);
  const [vendedores, setVendedores] = useState(null);

  const [sellerData, setSellerData] = useState(null);
  const [sellerData2, setSellerData2] = useState(null);
  const [sellerData3, setSellerData3] = useState(null);
  const [ticketStats, setTicketStats] = useState(null);
  const [posVendaData, setPosVendaData] = useState(null);
  const [posVendaCharts, setPosVendaCharts] = useState(null);

  const [visible, setVisible] = useState({
    vendas: true,
    vendedores: true,
    compras: true,
    logistica: true,
    produtos: false,
    posVenda: false,
    reputacao: true,
    tickets: true
  });

  const toggle = (key) => setVisible(prev => ({ ...prev, [key]: !prev[key] }));

  // --- ATUALIZAÇÃO AUTOMÁTICA DO MÓDULO COMERCIAL ---
  // A atualização é silenciosa: não volta para o skeleton e só troca o estado
  // quando o conteúdo mudou. Não dispara de novo enquanto a anterior não voltou.
  const emAndamentoRef = useRef({ hoje: false, periodos: false });
  const ultimaAtualizacaoRef = useRef({ hoje: 0, periodos: 0 });
  const [atualizadoEm, setAtualizadoEm] = useState(null);
  const [radarKey, setRadarKey] = useState(0);

  const pronto = (chave) => setLoadingMap(p => (p[chave] ? { ...p, [chave]: false } : p));
  const mesclarDadosGerais = (montar) => setDadosGerais(prev => seMudou(montar(prev))(prev));
  const serie = (res) => res?.labels?.map((l, i) => ({ date: l, Atual: res.vendasAtual[i], Anterior: res.vendasAnterior[i] }));

  // Vendas de hoje e equipe comercial (a cada 1 min)
  const carregarHoje = () => {
    if (emAndamentoRef.current.hoje) return Promise.resolve();
    emAndamentoRef.current.hoje = true;
    const tarefas = [];

    if (pode('vendas')) tarefas.push(getV3VendasHoje().then(res => {
      setVendasHoje(seMudou({
        total_vendido: res?.total_taga?.total_vendido || "0,00",
        total_vendas: res?.total_taga?.total_vendas || 0,
        ticket_medio: res?.total_taga?.ticket_medio || "0,00",
        canceladas: res?.total?.canceladas || 0,
        variacao_percentual: res?.total_taga?.variacao_percentual || 0,
        hourly_trend: res?.total_taga?.hourly_trend || []
      }));
      pronto('today');
    }).catch(err => console.error(err)));

    if (pode('vendedores')) tarefas.push(getV3Vendedores().then(res => {
      setVendedores(seMudou(res?.vendedores || []));
      pronto('vendedores');
    }).catch(err => {
      console.error(err);
      // Falha numa atualização mantém a lista que já está na tela.
      setVendedores(prev => (Array.isArray(prev) ? prev : false));
      pronto('vendedores');
    }));

    return Promise.all(tarefas).finally(() => {
      emAndamentoRef.current.hoje = false;
      ultimaAtualizacaoRef.current.hoje = Date.now();
      setAtualizadoEm(new Date());
    });
  };

  // Conversão, gráficos e dados gerais (a cada 5 min)
  const carregarPeriodos = () => {
    if (!pode('vendas') || emAndamentoRef.current.periodos) return Promise.resolve();
    emAndamentoRef.current.periodos = true;

    const tarefas = [
      getV3ConversaoML().then(res => {
        setConversaoML(seMudou(res || null));
        pronto('conversao');
      }).catch(err => {
        console.error(err);
        pronto('conversao');
      }),

      getV3GraficoUltimos30().then(res => {
        mesclarDadosGerais(prev => ({
          ...prev,
          trintaDias: { atual: res?.totalVendasAtual, anterior: res?.totalVendasAnterior, chart: serie(res) }
        }));
        pronto('trinta');
      }).catch(err => console.error(err)),

      // Preserva atual/anterior vindos dos dados gerais, que podem chegar antes.
      getV3GraficoSemanal().then(res => {
        mesclarDadosGerais(prev => ({ ...prev, semanal: { ...prev.semanal, chart: serie(res) } }));
        pronto('semanal');
      }).catch(err => console.error(err)),

      getV3GraficoMensal().then(res => {
        mesclarDadosGerais(prev => ({ ...prev, mensal: { ...prev.mensal, chart: serie(res) } }));
        pronto('mensal');
      }).catch(err => console.error(err)),

      getV3DadosGerais().then(res => {
        mesclarDadosGerais(prev => ({
          ...prev,
          dash: res || {},
          semanal: { ...prev.semanal, atual: res?.totalVendasSemanaAtual, anterior: res?.totalVendasSemanaAnterior },
          mensal: { ...prev.mensal, atual: res?.totalVendasMesAtual, anterior: res?.totalVendasMesAnterior },
          trintaDias: { ...prev.trintaDias, atual: res?.totalVendasAtual, anterior: res?.totalVendasAnterior },
          originData: res?.extraCharts?.originData || [],
          supplierData: res?.extraCharts?.supplierData || [],
          yearlyData: res?.extraCharts?.yearlyData || [],
          monthlyEvolution: res?.extraCharts?.monthlyEvolution || [],
          physicalStockData: res?.extraCharts?.physicalStockData || []
        }));
        pronto('geral');
      }).catch(err => console.error(err))
    ];

    return Promise.all(tarefas).finally(() => {
      emAndamentoRef.current.periodos = false;
      ultimaAtualizacaoRef.current.periodos = Date.now();
    });
  };

  // Demais blocos: carregam ao abrir e no botão de atualizar
  const carregarOutros = () => {
    // 4. Reputação
    if (pode('reputacao')) getDadosReputacao().then(res => {
      if (res) {
        setSellerData(res.conta1);
        setSellerData2(res.conta2);
        setSellerData3(res.conta3);
      }
      setLoadingMap(p => ({ ...p, reputacao: false }));
    }).catch(err => console.error(err));

    // 5. Tickets
    if (pode('tickets')) getTicketDashboardStats().then(res => {
      if (res) setTicketStats(res);
      setLoadingMap(p => ({ ...p, tickets: false }));
    }).catch(err => {
      console.error(err);
      setLoadingMap(p => ({ ...p, tickets: false }));
    });

    if (pode('logistica')) getV3Logistica().then(res => {
      setDadosLogistica(res || {});
      setLoadingMap(p => ({ ...p, logistica: false }));
    }).catch(console.error);

    if (pode('produtos')) getV3Produtos().then(res => {
      setDadosProdutos(res || {});
      setLoadingMap(p => ({ ...p, produtos: false }));
    }).catch(console.error);

    if (pode('compras')) getV3Compras().then(res => {
      setDadosCompras(res || {});
      setLoadingMap(p => ({ ...p, compras: false }));
    }).catch(err => {
      console.error(err);
      setLoadingMap(p => ({ ...p, compras: false }));
    });

    // 6. Pós-Venda (reclamações + devoluções, últimos 12 meses)
    const somaVendas = (arr) => (arr || []).reduce((s, m) => s + (m.vendas_qtd || 0), 0);
    const normLabel = (l) => String(l || '').replace(/\./g, '').toLowerCase();

    if (pode('posVenda')) Promise.all([
      getReclamacoesMetricas({}),
      getDevolucoesMetricas('')
    ]).then(([recl, dev]) => {
      const reclVs = recl?.vendas_vs_reclamacoes || [];
      const devVs = dev?.vendas_vs_devolucoes || [];
      const reclVol = recl?.volume_mensal || [];
      const devVol = dev?.volume_mensal || [];

      const vendasRecl = somaVendas(reclVs);
      const vendasDev = somaVendas(devVs);
      const devDevolvidas = devVs.reduce((s, m) => s + (m.devolvidas_qtd || 0), 0);

      setPosVendaData({
        reclAbertas: recl?.kpis?.abertas ?? null,
        taxaRecl: vendasRecl > 0 ? (recl?.kpis?.total || 0) / vendasRecl * 100 : null,
        devTotal: dev?.kpis?.total_devolucoes ?? null,
        taxaDev: vendasDev > 0 ? devDevolvidas / vendasDev * 100 : null
      });

      const taxaTrend = reclVs.map((m, i) => ({
        label: m.label,
        Pedidos: m.vendas_qtd ?? 0,
        Reclamação: m.taxa_qtd ?? 0,
        Devolução: devVs[i]?.taxa_qtd ?? 0
      }));

      const reclVolMap = Object.fromEntries(reclVol.map(m => [normLabel(m.label), m.abertas ?? 0]));
      const devVolMap = Object.fromEntries(devVol.map(m => [normLabel(m.label), m.qtd ?? 0]));
      const volumeTrend = reclVs.map((m) => ({
        label: m.label,
        Pedidos: m.vendas_qtd ?? 0,
        Reclamações: reclVolMap[normLabel(m.label)] ?? 0,
        Devoluções: devVolMap[normLabel(m.label)] ?? 0
      }));

      const topMotivos = (arr) => (arr || []).slice(0, 6).map(x => ({ motivo: x.motivo, qtd: x.qtd }));

      setPosVendaCharts({
        taxaTrend,
        volumeTrend,
        motivosRecl: topMotivos(recl?.top_motivos),
        motivosDev: topMotivos(dev?.top_motivos_reais)
      });
      setLoadingMap(p => ({ ...p, posVenda: false }));
    }).catch(err => {
      console.error(err);
      setLoadingMap(p => ({ ...p, posVenda: false }));
    });
  };

  useEffect(() => {
    if (dataFetchedRef.current) return;
    dataFetchedRef.current = true;
    carregarHoje();
    carregarPeriodos();
    carregarOutros();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Relógio da atualização: pausa com a aba em segundo plano (ou celular
  // bloqueado) e, ao voltar, atualiza na hora o que estiver vencido.
  useEffect(() => {
    if (!pode('vendas') && !pode('vendedores')) return undefined;

    const verificar = () => {
      if (document.visibilityState !== 'visible') return;
      const agora = Date.now();
      // Folga de 5 s para o timer não pular um ciclo por milissegundos.
      if (agora - ultimaAtualizacaoRef.current.hoje >= ATUALIZAR_HOJE_MS - 5000) carregarHoje();
      if (agora - ultimaAtualizacaoRef.current.periodos >= ATUALIZAR_PERIODOS_MS - 5000) carregarPeriodos();
    };

    const timer = setInterval(verificar, ATUALIZAR_HOJE_MS);
    document.addEventListener('visibilitychange', verificar);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', verificar);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const atualizarTudo = () => {
    if (reloading) return;
    setReloading(true);
    setRadarKey(k => k + 1);
    carregarOutros();
    Promise.all([carregarHoje(), carregarPeriodos()]).finally(() => setReloading(false));
  };

  const statusFormatado = dadosGerais.dash?.totalVendasPorStatus ? [
    { name: "Confirmadas", value: dadosGerais.dash.totalVendasPorStatus[0] },
    { name: "Para Enviar", value: dadosGerais.dash.totalVendasPorStatus[1] },
    { name: "Em Trânsito", value: dadosGerais.dash.totalVendasPorStatus[2] },
    { name: "Concluídas", value: dadosGerais.dash.totalVendasPorStatus[3] },
    { name: "Canceladas", value: dadosGerais.dash.totalVendasPorStatus[4] }
  ] : [];

  const renderExpandedContent = () => {
    const d = dadosGerais;
    const hTrend = vendasHoje.hourly_trend || [];
    const origin = d.originData || [];
    const status = statusFormatado || [];
    const evolucao = d.monthlyEvolution || [];
    const anual = d.yearlyData || [];
    const fisico = d.physicalStockData || [];
    const supplier = d.supplierData || [];
    const trinta = d.trintaDias?.chart || [];
    const semana = d.semanal?.chart || [];
    const mes = d.mensal?.chart || [];

    const prodTop20 = (dadosProdutos.top20_vendidos || []).map(it => ({ ...it, label: `(${it.sku || '?'}) - ${it.name}` }));
    const prodReceita = (dadosProdutos.top10_receita || []).map(it => ({ ...it, label: `(${it.sku || '?'}) - ${it.name}` }));
    const prodFuro = (dadosProdutos.top10_furo || []).map(it => ({ ...it, label: `(${it.sku || '?'}) - ${it.name}` }));

    switch (expandedBlock) {
      case 'faturamento': return <AreaChart data={hTrend}><XAxis dataKey="hour" /><YAxis /><Tooltip /><Area type="monotone" dataKey="v" stroke="#f97316" fill="#f97316" fillOpacity={0.1} /></AreaChart>;
      case 'origem': return <BarChart layout="vertical" data={origin} margin={{ left: 20 }}><XAxis type="number" /><YAxis type="category" dataKey="name" width={150} /><Tooltip content={<CustomDualTooltip />} /><Bar dataKey="value" barSize={30}>{origin.map((e, i) => (<Cell key={i} fill={ORIGIN_COLORS[i % 4]} />))}</Bar></BarChart>;
      case 'fornecedor': return <BarChart layout="vertical" data={supplier} margin={{ left: 20 }}><XAxis type="number" /><YAxis type="category" dataKey="name" width={150} /><Tooltip content={<CustomDualTooltip />} /><Bar dataKey="value" barSize={30}>{supplier.map((e, i) => (<Cell key={i} fill={SUPPLIER_COLORS[i % SUPPLIER_COLORS.length]} />))}</Bar></BarChart>;
      case 'status': return <BarChart data={status}><XAxis dataKey="name" /><YAxis /><Tooltip /><Bar dataKey="value" fill="#8b5cf6" /></BarChart>;
      case 'evolucao': return <AreaChart data={evolucao}><XAxis dataKey="mes" /><YAxis /><Tooltip formatter={(v) => formatMoney(v)} /><Area type="monotone" dataKey="Ano Atual" stroke="#8b5cf6" fill="#ddd6fe" /><Area type="monotone" dataKey="Ano Anterior" stroke="#cbd5e1" /></AreaChart>;
      case 'anual': return <BarChart data={anual}><XAxis dataKey="ano" /><YAxis /><Tooltip formatter={(v) => formatMoney(v)} /><Bar dataKey="total" fill="#3b82f6" /></BarChart>;
      case 'estoque_fisico': return <BarChart data={fisico}><XAxis dataKey="mes" /><YAxis /><Tooltip content={<CustomStockTooltip />} /><Bar dataKey="valor" fill="#10b981" /></BarChart>;
      case '30dias': return <AreaChart data={trinta}><XAxis dataKey="date" /><YAxis /><Tooltip /><Area type="monotone" dataKey="Atual" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} /><Area type="monotone" dataKey="Anterior" stroke="#cbd5e1" /></AreaChart>;
      case 'semanal': return <AreaChart data={semana}><XAxis dataKey="date" /><YAxis /><Tooltip /><Area type="monotone" dataKey="Atual" stroke="#f97316" fill="#f97316" fillOpacity={0.1} /><Area type="monotone" dataKey="Anterior" stroke="#cbd5e1" /></AreaChart>;
      case 'mensal': return <AreaChart data={mes}><XAxis dataKey="date" /><YAxis /><Tooltip /><Area type="monotone" dataKey="Atual" stroke="#10b981" fill="#10b981" fillOpacity={0.1} /><Area type="monotone" dataKey="Anterior" stroke="#cbd5e1" /></AreaChart>;
      case 'compras_evolucao': {
        const ev = (dadosCompras.evolucao || []).map(e => ({ ...e, label: e.parcial ? `${e.label}*` : e.label }));
        const ser = dadosCompras.series_grafico || [];
        return (
          <BarChart data={ev} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748b' }} />
            <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} tickFormatter={formatMoneyCurto} width={70} />
            <Tooltip content={<ComprasTooltip />} cursor={{ fill: '#f8fafc' }} />
            <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            {ser.map((nome, i) => (
              <Bar key={nome} dataKey={nome} stackId="compras" fill={COMPRAS_COLORS[i % COMPRAS_COLORS.length]} radius={i === ser.length - 1 ? [4, 4, 0, 0] : 0} />
            ))}
          </BarChart>
        );
      }
      case 'compras_ranking': {
        const rk = (dadosCompras.ranking || []).map(f => ({ ...f, value: f.total }));
        return (
          <BarChart data={rk} layout="vertical" margin={{ left: 20, right: 30 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
            <XAxis type="number" tickFormatter={formatMoneyCurto} />
            <YAxis dataKey="nome" type="category" width={180} tick={{ fontSize: 12, fill: '#64748b' }} interval={0} />
            <Tooltip cursor={{ fill: '#f8fafc' }} content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const f = payload[0]?.payload || {};
              return (
                <div className="bg-white border border-slate-200 shadow-lg rounded-lg p-3">
                  <p className="text-xs font-bold text-slate-700 mb-1">{f.nome}</p>
                  <p className="text-xs text-indigo-600 font-semibold">{formatMoney(f.total)} · {f.participacao}% do período</p>
                  <p className="text-xs text-slate-500">{f.itens} itens · {f.listas} listas</p>
                  <p className="text-xs text-slate-500">Mês: {formatMoney(f.mes_atual)} · anterior até o dia: {formatMoney(f.mes_anterior_ate_dia)}</p>
                </div>
              );
            }} />
            <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={22}>
              {rk.map((f, i) => (<Cell key={i} fill={COMPRAS_COLORS[i % COMPRAS_COLORS.length]} />))}
            </Bar>
          </BarChart>
        );
      }
      case 'prod_top20': return <BarChart data={prodTop20} layout="vertical" margin={{ left: 20, right: 30 }}><CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" /><XAxis type="number" /><YAxis dataKey="label" type="category" width={360} /><Tooltip content={({ active, payload }) => { if (!active || !payload?.length) return null; const p = payload[0]?.payload || {}; return (<div className="bg-white border border-slate-200 shadow-lg rounded-lg p-3"><p className="text-xs font-bold text-slate-700 mb-1">{p.label}</p><p className="text-xs text-indigo-600 font-semibold">{payload[0]?.value} unidades</p></div>); }} /><Bar dataKey="value" radius={[0, 4, 4, 0]}>{prodTop20.map((_, i) => (<Cell key={i} fill={`hsl(${243 - i * 4}, 65%, ${62 - i * 1.2}%)`} />))}</Bar></BarChart>;
      case 'prod_receita': return <BarChart data={prodReceita} layout="vertical" margin={{ left: 20, right: 30 }}><CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" /><XAxis type="number" tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} /><YAxis dataKey="label" type="category" width={360} /><Tooltip content={({ active, payload }) => { if (!active || !payload?.length) return null; const p = payload[0]?.payload || {}; return (<div className="bg-white border border-slate-200 shadow-lg rounded-lg p-3"><p className="text-xs font-bold text-slate-700 mb-1">{p.label}</p><p className="text-xs text-emerald-600 font-semibold">{formatMoney(payload[0]?.value)}</p><p className="text-xs text-slate-500">{p.qtd} un vendidas</p></div>); }} /><Bar dataKey="value" radius={[0, 4, 4, 0]}>{prodReceita.map((_, i) => (<Cell key={i} fill={`hsl(${158 + i * 3}, 58%, ${46 + i * 1.5}%)`} />))}</Bar></BarChart>;
      case 'prod_furo': return <BarChart data={prodFuro} layout="vertical" margin={{ left: 20, right: 30 }}><CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" /><XAxis type="number" /><YAxis dataKey="label" type="category" width={360} /><Tooltip content={({ active, payload }) => { if (!active || !payload?.length) return null; const p = payload[0]?.payload || {}; return (<div className="bg-white border border-slate-200 shadow-lg rounded-lg p-3"><p className="text-xs font-bold text-slate-700 mb-1">{p.label}</p><p className="text-xs text-orange-600 font-semibold">{payload[0]?.value} unidades</p></div>); }} /><Bar dataKey="value" radius={[0, 4, 4, 0]}>{prodFuro.map((_, i) => (<Cell key={i} fill={`hsl(${20 + i * 5}, 85%, ${55 - i * 2}%)`} />))}</Bar></BarChart>;
      default: return null;
    }
  };

  return (
    <>
      <ScrollToTop />
      <main className="bg-slate-50 min-h-screen py-3 px-4 md:py-4 md:px-8 ml-0 md:ml-20 transition-all duration-300 relative">
        {expandedBlock && (
          <div className="fixed inset-0 z-[999] bg-white p-4 md:p-10 flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <Title className="text-xl md:text-2xl font-bold uppercase text-slate-800">Visualização Expandida</Title>
              <button onClick={() => setExpandedBlock(null)} className="p-2 bg-slate-100 hover:bg-red-100 text-slate-500 hover:text-red-600 rounded-full transition-all">
                <X size={24} />
              </button>
            </div>
            <div className="flex-1 bg-slate-50 rounded-2xl p-4 md:p-8 border border-slate-200">
              <ResponsiveContainer width="100%" height="100%">{renderExpandedContent()}</ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="max-w-[1600px] mx-auto">
          <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 border-b border-slate-200 pb-2 gap-4">
            <Title className="text-2xl md:text-3xl font-black text-slate-800 tracking-tight uppercase">Dashboard</Title>
            <div className="flex items-center gap-3 self-end sm:self-auto">
              {atualizadoEm && (
                <span className="text-xs text-slate-400" aria-live="polite">
                  Atualizado às {atualizadoEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              <button
                onClick={atualizarTudo}
                disabled={reloading}
                aria-label="Atualizar dashboard"
                className="group p-2 bg-white border border-slate-200 rounded-lg text-slate-500 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-500 hover:shadow-md active:scale-95 transition-all shadow-sm disabled:cursor-wait"
              >
                <RefreshCw size={20} className={`transition-transform ${reloading ? 'animate-spin' : 'group-hover:animate-spin'}`} />
              </button>
            </div>
          </header>

          {pode('radar') && <RadarDashboard key={radarKey} />}

          {blocos.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-6 text-center text-slate-500 text-sm">
              Nenhum bloco da dashboard está disponível para o seu perfil.
            </div>
          )}

          {/* VENDAS (LOADING INCREMENTAL) */}
          {pode('vendas') && (
            <section className="mb-12">
              <div onClick={() => toggle('vendas')} className="cursor-pointer group flex items-center gap-3 mb-4 transition-all">
                <div className="p-1 bg-slate-200 group-hover:bg-orange-500 group-hover:text-white rounded transition-colors">
                  {visible.vendas ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                </div>
                <div>
                  <Text className="text-orange-600 font-bold uppercase tracking-widest text-xs">Módulo Comercial</Text>
                  <Title className="text-2xl font-black text-slate-700 leading-none">Vendas</Title>
                </div>
              </div>
              {visible.vendas && (
                <div className="animate-in fade-in slide-in-from-top-4 duration-300">
                  <TodaySalesSection data={vendasHoje} loading={loadingMap.today} setExpanded={setExpandedBlock} />
                  <ConversionMLSection data={conversaoML} loading={loadingMap.conversao} />
                  <ComparisonChartsSection data={dadosGerais} loadingMap={loadingMap} setExpanded={setExpandedBlock} />
                  <SalesDetailedAnalyticsSection originData={dadosGerais.originData} supplierData={dadosGerais.supplierData} loading={loadingMap.geral} setExpanded={setExpandedBlock} />
                  <OrdersStatusAndPendingSection statusData={statusFormatado} pendingData={dadosGerais.dash} loading={loadingMap.geral} setExpanded={setExpandedBlock} />
                  <LongTermAnalyticsSection monthlyData={dadosGerais.monthlyEvolution} yearlyData={dadosGerais.yearlyData} physicalData={dadosGerais.physicalStockData} loading={loadingMap.geral} setExpanded={setExpandedBlock} />
                </div>
              )}
            </section>
          )}

          {/* VENDEDORES */}
          {pode('vendedores') && (
            <section className="mb-12">
              <div onClick={() => toggle('vendedores')} className="cursor-pointer group flex items-center gap-3 mb-6 transition-all">
                <div className="p-1 bg-slate-200 group-hover:bg-orange-500 group-hover:text-white rounded transition-colors">
                  {visible.vendedores ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                </div>
                <div>
                  <Text className="text-orange-600 font-bold uppercase tracking-widest text-xs">Equipe Comercial</Text>
                  <Title className="text-2xl font-black text-slate-700 leading-none">Vendedores</Title>
                </div>
              </div>
              {visible.vendedores && (
                <div className="animate-in fade-in slide-in-from-top-4 duration-300">
                  <DashboardVendedores vendedores={vendedores} loading={loadingMap.vendedores} />
                </div>
              )}
            </section>
          )}


          {/* COMPRAS */}
          {pode('compras') && (
            <section className="mb-12">
              <div onClick={() => toggle('compras')} className="cursor-pointer group flex items-center gap-3 mb-6 transition-all">
                <div className="p-1 bg-slate-200 group-hover:bg-indigo-500 group-hover:text-white rounded transition-colors">
                  {visible.compras ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                </div>
                <div>
                  <Text className="text-indigo-600 font-bold uppercase tracking-widest text-xs">Suprimentos</Text>
                  <Title className="text-2xl font-black text-slate-700 leading-none">Compras</Title>
                </div>
              </div>
              {visible.compras && (
                <div className="animate-in fade-in slide-in-from-top-4 duration-300">
                  <PurchasesModule data={dadosCompras} loading={loadingMap.compras} setExpanded={setExpandedBlock} />
                </div>
              )}
            </section>
          )}

          {/* LOGÍSTICA */}
          {pode('logistica') && (
            <section className="mb-12">
              <div onClick={() => toggle('logistica')} className="cursor-pointer group flex items-center gap-3 mb-6 transition-all">
                <div className="p-1 bg-slate-200 group-hover:bg-blue-500 group-hover:text-white rounded transition-colors">
                  {visible.logistica ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                </div>
                <div>
                  <Text className="text-blue-600 font-bold uppercase tracking-widest text-xs">Módulo Operacional</Text>
                  <Title className="text-2xl font-black text-slate-700 leading-none">Logística</Title>
                </div>
              </div>
              {visible.logistica && (
                <div className="animate-in fade-in slide-in-from-top-4 duration-300">
                  <LogisticsModule data={dadosLogistica} loading={loadingMap.logistica} />
                </div>
              )}
            </section>
          )}

          {/* PRODUTOS */}
          {pode('produtos') && (
            <section className="mb-12">
              <div onClick={() => toggle('produtos')} className="cursor-pointer group flex items-center gap-3 mb-6 transition-all">
                <div className="p-1 bg-slate-200 group-hover:bg-pink-500 group-hover:text-white rounded transition-colors">
                  {visible.produtos ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                </div>
                <div>
                  <Text className="text-pink-600 font-bold uppercase tracking-widest text-xs">Gestão de Inventário</Text>
                  <Title className="text-2xl font-black text-slate-700 leading-none">Produtos</Title>
                </div>
              </div>
              {visible.produtos && (
                <div className="animate-in fade-in slide-in-from-top-4 duration-300">
                  <ProductsModule data={dadosProdutos} loading={loadingMap.produtos} setExpanded={setExpandedBlock} />
                </div>
              )}
            </section>
          )}

          {/* PÓS-VENDA */}
          {pode('posVenda') && (
            <section className="mb-12">
              <div onClick={() => toggle('posVenda')} className="cursor-pointer group flex items-center gap-3 mb-6 transition-all">
                <div className="p-1 bg-slate-200 group-hover:bg-violet-500 group-hover:text-white rounded transition-colors">
                  {visible.posVenda ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                </div>
                <div>
                  <Text className="text-violet-600 font-bold uppercase tracking-widest text-xs">Qualidade</Text>
                  <Title className="text-2xl font-black text-slate-700 leading-none">Pós-Venda</Title>
                </div>
              </div>
              {visible.posVenda && <div className="animate-in fade-in slide-in-from-top-4 duration-300"><AfterSalesModule data={posVendaData} charts={posVendaCharts} loading={loadingMap.posVenda} /></div>}
            </section>
          )}

          {/* TICKETS (DADOS REAIS COM SKELETON) */}
          {pode('tickets') && (
            <section className="mb-12">
              <div onClick={() => toggle('tickets')} className="cursor-pointer group flex items-center gap-3 mb-6 transition-all">
                <div className="p-1 bg-slate-200 group-hover:bg-cyan-500 group-hover:text-white rounded transition-colors">
                  {visible.tickets ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                </div>
                <div>
                  <Text className="text-cyan-600 font-bold uppercase tracking-widest text-xs">Atendimento</Text>
                  <Title className="text-2xl font-black text-slate-700 leading-none">Tickets e Suporte</Title>
                </div>
              </div>
              {visible.tickets && (
                <div className="animate-in fade-in slide-in-from-top-4 duration-300">
                  <Box maxW="100%" mx="auto">
                    <TicketSummaryDashboard stats={ticketStats} loading={loadingMap.tickets} />
                  </Box>
                </div>
              )}
            </section>
          )}

          {/* REPUTAÇÃO MERCADO LIVRE (DADOS REAIS COM SKELETON) */}
          {pode('reputacao') && (
            <section className="mb-12">
              <div onClick={() => toggle('reputacao')} className="cursor-pointer group flex items-center gap-3 mb-6 transition-all">
                <div className="p-1 bg-slate-200 group-hover:bg-yellow-500 group-hover:text-white rounded transition-colors">
                  {visible.reputacao ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                </div>
                <div>
                  <Text className="text-yellow-600 font-bold uppercase tracking-widest text-xs">Mercado Livre</Text>
                  <Title className="text-2xl font-black text-slate-700 leading-none">Reputação das Contas</Title>
                </div>
              </div>
              {visible.reputacao && (
                <div className="animate-in fade-in slide-in-from-top-4 duration-300">
                  <Skeleton isLoaded={!loadingMap.reputacao} minHeight="200px" borderRadius="0.5rem">
                    <Stack
                      mt={4}
                      direction={{ base: "column", xl: "row" }}
                      spacing={6}
                      align="stretch"
                      justify="center"
                    >
                      <Box w="100%">
                        {sellerData && Object.keys(sellerData).length > 0 && <SellerAccountInfoML sellerData={sellerData} conta={1} />}
                      </Box>
                      <Box w="100%">
                        {sellerData2 && Object.keys(sellerData2).length > 0 && <SellerAccountInfoML sellerData={sellerData2} conta={2} />}
                      </Box>
                    </Stack>
                    {sellerData3 && Object.keys(sellerData3).length > 0 && (
                      <Box mt={6} w={{ base: "100%", xl: "50%" }} pr={{ xl: 3 }}>
                        <SellerAccountInfoML sellerData={sellerData3} conta={3} />
                      </Box>
                    )}
                  </Skeleton>
                </div>
              )}
            </section>
          )}

        </div>
      </main>
    </>
  );
};

export default DashboardV3;